import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { adminApi } from '@/services/api'
import { secureStorage } from '@/utils/secureStorage'

export interface LoyaltyLevel {
  name: string
  min_points: number
}

export type ProjectPointsScope = 'establishment' | 'project'
export type ProjectPointsMode = 'card' | 'order'

export interface ProjectSummary {
  uuid: string
  public_key: string
  slug?: string
  name: string
  is_active: boolean
  points_mode?: ProjectPointsMode
  points_scope?: ProjectPointsScope
  loyalty_levels?: LoyaltyLevel[]
  referral_enabled?: boolean
  referral_referrer_points?: number
  referral_referee_points?: number
  membership_role?: 'project_owner' | 'contributor' | 'viewer'
}

export const useProjectContextStore = defineStore('projectContext', () => {
  const projects = ref<ProjectSummary[]>([])
  const activeProjectId = ref<string | null>(secureStorage.getActiveProjectId())
  const projectsLoaded = ref(false)

  const setActiveProject = (projectId: string | null) => {
    activeProjectId.value = projectId
    if (projectId) {
      secureStorage.setActiveProjectId(projectId)
    } else {
      secureStorage.removeActiveProjectId()
    }
  }

  function syncActiveProjectWithList() {
    if (projects.value.length === 0) {
      if (activeProjectId.value) {
        setActiveProject(null)
      }
      return
    }
    const ids = new Set(projects.value.map((p) => p.uuid))
    if (!activeProjectId.value || !ids.has(activeProjectId.value)) {
      setActiveProject(projects.value[0].uuid)
    }
  }

  let projectListFetchPromise: Promise<void> | null = null
  let projectListFetchMode: 'superadmin' | 'assigned' | null = null

  async function runProjectListFetch(
    useSuperadminList: boolean,
    options?: { force?: boolean },
  ) {
    const mode = useSuperadminList ? 'superadmin' : 'assigned'

    if (!options?.force && projectsLoaded.value) {
      return
    }

    if (!options?.force && projectListFetchPromise && projectListFetchMode === mode) {
      return projectListFetchPromise
    }

    projectListFetchMode = mode
    projectListFetchPromise = (async () => {
      try {
        const data = useSuperadminList ? await adminApi.getProjects() : await adminApi.getMyProjects()
        projects.value = Array.isArray(data) ? data : []
        syncActiveProjectWithList()
      } catch {
        projects.value = []
        syncActiveProjectWithList()
      } finally {
        projectsLoaded.value = true
        projectListFetchPromise = null
      }
    })()

    return projectListFetchPromise
  }

  const fetchSuperadminProjectList = async (options?: { force?: boolean }) => {
    await runProjectListFetch(true, options)
  }

  const fetchMyAssignedProjects = async (options?: { force?: boolean }) => {
    await runProjectListFetch(false, options)
  }

  const clearProjectContext = () => {
    projects.value = []
    activeProjectId.value = null
    projectsLoaded.value = false
    projectListFetchPromise = null
    projectListFetchMode = null
    secureStorage.removeActiveProjectId()
  }

  const createProject = async (payload: { name: string }) => {
    const created = await adminApi.createProject( payload )
    await fetchSuperadminProjectList({ force: true })
    if (created?.uuid) {
      setActiveProject(created.uuid)
    }
    return created
  }

  const deleteProject = async (projectId: string) => {
    await adminApi.deleteProject(projectId)
    await fetchSuperadminProjectList({ force: true })
    if (activeProjectId.value === projectId) {
      const nextProject = projects.value[0]?.uuid ?? null
      setActiveProject(nextProject)
    }
  }

  const activeProject = computed(() => {
    const id = activeProjectId.value
    if (!id) return null
    return projects.value.find((x) => x.uuid === id) ?? null
  })

  const activeProjectMembershipRole = computed(() => {
    return activeProject.value?.membership_role ?? null
  })

  const updateProject = async (
    projectUuid: string,
    payload: {
      name?: string
      points_mode?: ProjectPointsMode
      points_scope?: ProjectPointsScope
      loyalty_levels?: LoyaltyLevel[]
      referral_enabled?: boolean
      referral_referrer_points?: number
      referral_referee_points?: number
    },
  ) => {
    const updated = await adminApi.updateProject(projectUuid, payload)
    const idx = projects.value.findIndex((p) => p.uuid === projectUuid)
    if (idx !== -1) {
      projects.value[idx] = { ...projects.value[idx], ...updated }
    }
    return updated
  }

  return {
    projects,
    activeProjectId,
    activeProject,
    activeProjectMembershipRole,
    projectsLoaded,
    fetchSuperadminProjectList,
    fetchMyAssignedProjects,
    setActiveProject,
    clearProjectContext,
    createProject,
    updateProject,
    deleteProject,
  }
})
